import { type ForwardedRef, forwardRef, useCallback, useImperativeHandle } from "react";
import { DialogModal } from "./DialogModal";
import type { ModalHandle, ModalProps } from "./types";

/**
 * Modal component that adapts to platform.
 * On small screens, becomes full-screen. On large screens, shows as centered dialog.
 *
 * @example
 * <Modal
 *   open={open}
 *   onOpenChange={setOpen}
 *   headerProps={{ title: "Settings", variant: "titleSubtitle", subtitle: "Customize" }}
 *   footerProps={{ primaryButtonProps: { children: "Save" } }}
 * >
 *   <Text>Content</Text>
 * </Modal>
 */
export const Modal = forwardRef(function Modal({
  open,
  onOpenChange,
  onDismiss,
  children,
  gradientBackground,
  headerProps,
  footerProps,
  size = "medium",
  scrollable = true,
  animateHeight = true,
  maxHeight,
  fillHeight
}: ModalProps, ref: ForwardedRef<ModalHandle>) {
  // onDismiss fires on user closes (X, scrim, Escape) — not on imperative dismiss().
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      onDismiss?.();
    }
    onOpenChange(nextOpen);
  }, [onOpenChange, onDismiss]);

  // Web has no animation to coordinate with, so dismiss is just a state flip.
  useImperativeHandle(ref, () => ({
    dismiss: () => onOpenChange(false)
  }), [onOpenChange]);
  return <DialogModal open={open} onOpenChange={handleOpenChange} size={size} gradientBackground={gradientBackground} headerProps={headerProps} footerProps={footerProps} scrollable={scrollable} animateHeight={animateHeight} maxHeight={maxHeight} fillHeight={fillHeight}>
      {children}
    </DialogModal>;
});